import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

/** What a skeleton cell draws, standing in for the real cell of that column. */
export type SkeletonCellKind = 'check' | 'entity' | 'title' | 'text' | 'date' | 'pill' | 'amount' | 'avatar' | 'icon' | 'empty';

export interface SkeletonColumn {
  /** Header caption; leave out for an unlabelled column (checkbox, menu). */
  label?: string;
  /** CSS grid track, e.g. '44px' or 'minmax(220px, 3fr)'. */
  width: string;
  kind: SkeletonCellKind;
  align?: 'start' | 'end' | 'center';
  /** Dropped at ≤768px, as the real tables drop secondary columns. */
  hideOnMobile?: boolean;
}

/**
 * SD-1235: the loading state for the list tables — the Automate > Workflows skeleton
 * (automate/_list-page.scss): the same framed card and tinted uppercase header as the loaded
 * table, rows the shape of real ones fading in one after another under a slow shimmer, so
 * nothing jumps when the data lands.
 *
 *   <app-list-skeleton [columns]="skeletonColumns"></app-list-skeleton>
 *
 * Inside a table that already draws its own frame and header, pass [framed]="false".
 */
@Component({
  selector: 'app-list-skeleton',
  templateUrl: './list-skeleton.component.html',
  styleUrls: ['./list-skeleton.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ListSkeletonComponent {
  @Input() set columns(value: SkeletonColumn[]) {
    this._columns = value || [];
    this.gridDesktop = this._columns.map(c => c.width).join(' ');
    this.gridMobile = this._columns.filter(c => !c.hideOnMobile).map(c => c.width).join(' ');
  }
  get columns(): SkeletonColumn[] { return this._columns; }

  @Input() set rows(count: number) {
    this.rowIndexes = Array.from({ length: Math.max(1, count || 0) }, (_, i) => i);
  }

  /** Draw the card and its header; off when the host table already does. */
  @Input() framed = true;

  rowIndexes = Array.from({ length: 8 }, (_, i) => i);
  gridDesktop = '';
  gridMobile = '';

  // Widths per row, so the column reads as text of different lengths rather than a grid of bars.
  readonly titleWidths = [62, 48, 70, 55, 66, 44, 58, 51];
  readonly subWidths = [34, 40, 28, 38, 30, 36, 32, 42];
  readonly textWidths = [72, 56, 64, 80, 50, 68, 60, 76];

  private _columns: SkeletonColumn[] = [];

  trackByIndex(index: number): number { return index; }
}
